import { Divider, Icon, Typography } from '@scale/scaleui';
import { SquadMemberRole, SquadType } from '@scale/shared/types/expertManagement';
import React, { useCallback, useMemo, useState } from 'react';
import { EditSquadInfoModal } from 'react-scale/components/ExpertManagement/CommunityManagement/EditSquadInfoModal';
import { SquadMemberTable } from 'react-scale/components/ExpertManagement/CommunityManagement/SquadMemberTable';
import {
  CommunityChannelLink,
  SQUAD_TYPE_TO_NAME_MAP,
} from 'react-scale/components/ExpertManagement/CommunityManagement/SquadsManagement';
import { SlidingDrawer } from 'react-scale/components/ExpertManagement/SlidingDrawer';
import { useFetchSquadById } from 'react-scale/components/ExpertManagement/hooks/queries/useFetchSquadById';
import {
  podTypesSelector,
  useStatContextStore,
} from 'react-scale/components/ExpertManagement/stores/statContext.store';
import { shallow } from 'zustand/shallow';
import { Text, Flex } from '@scale/scaleui-radix';

const roleSort: Record<SquadMemberRole, number> = {
  [SquadMemberRole.Contributor]: 2,
  [SquadMemberRole.TeamLead]: 1,
  [SquadMemberRole.TeamLeadCoordinator]: 0,
};

type EditSquadDrawerProps = {
  editMode: 'edit' | 'create';
  loading: boolean;
  onClose: () => void;
  open: boolean;
  refetchSquads: () => Promise<any>;
  squad: any;
};

export const EditSquadDrawer: React.FC<EditSquadDrawerProps> = ({
  editMode,
  loading,
  onClose,
  open,
  refetchSquads,
  squad,
}) => {
  const podTypeOptions = useStatContextStore(podTypesSelector, shallow);
  const [showEditSquadInfoModal, setShowEditSquadInfoModal] = useState(false);

  const {
    data: squadData,
    isLoading: squadLoading,
    refetch: refetchSquad,
  } = useFetchSquadById(squad?._id);

  const sortedSquadMembers = useMemo(() => {
    if (!squadData) return [];
    return squadData.members
      .sort((a: any, b: any) => a.name.localeCompare(b.name))
      .sort(
        (a: any, b: any) =>
          roleSort[a.role as SquadMemberRole] - roleSort[b.role as SquadMemberRole],
      );
  }, [squadData]);

  const keyedPodTypes = useMemo(
    () => Object.fromEntries(podTypeOptions.map(p => [p._id, p.name])),
    [podTypeOptions],
  );

  const refetch = useCallback(async () => {
    refetchSquads();
    refetchSquad();
  }, [refetchSquads, refetchSquad]);

  return (
    <>
      <SlidingDrawer
        canSave={false}
        open={open}
        onClose={onClose}
        onOutsideClick={onClose}
        component={
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <div className="flex justify-between">
                <Typography.H3>{squad?.name}</Typography.H3>
                <Icon
                  className="cursor-pointer"
                  icon="edit"
                  onClick={() => setShowEditSquadInfoModal(true)}
                />
              </div>
              <div className="flex gap-4">
                <Typography.Subtitle4>
                  {SQUAD_TYPE_TO_NAME_MAP[squad?.type as SquadType]}
                </Typography.Subtitle4>
                <Typography.Subtitle4>
                  {keyedPodTypes[squad?.teamLeadPodType ?? '']}
                </Typography.Subtitle4>
                <Typography.Subtitle4>Squad ID: {squad?._id}</Typography.Subtitle4>
                {squad?.communityChannel && (
                  <Flex gap="1" align="center">
                    <Text weight="medium" size="1">
                      Channel ID:
                    </Text>
                    <CommunityChannelLink communityChannel={squad.communityChannel} />
                  </Flex>
                )}
              </div>
            </div>
            <Divider />
            <SquadMemberTable
              data={sortedSquadMembers}
              isLoading={squadLoading}
              squadId={squad?._id ?? ''}
              refetch={refetch}
              squadType={squad?.type ?? SquadType.Squad}
            />
          </div>
        }
        disableButtons={loading}
        allowDelete={false}
        saveButtonText={editMode === 'edit' ? 'Save Changes' : 'Create'}
        width="70%"
      />
      <EditSquadInfoModal
        open={showEditSquadInfoModal}
        onClose={() => setShowEditSquadInfoModal(false)}
        refetch={refetchSquads}
        squad={squad}
      />
    </>
  );
};
